import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'
import { formatMCNumber } from '../services/rtsService'
import { RTSRatingData, setRTSRating } from '../store/loadSearchSlice'
import { RootState } from '../store'

/**
 * Hook to fetch and manage RTS rating data via the per-user main-service endpoint
 * @param mcNumber The MC number to fetch the rating for
 * @returns Object containing the rating data and loading state
 */
export const useRTSRating = (mcNumber?: string) => {
    const dispatch = useDispatch()
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<Error | null>(null)

    // Get the rating data from Redux store
    const rtsRatings = useSelector((state: RootState) => state.loadSearch.rtsRatings)

    useEffect(() => {
        // If no MC number, return early
        if (!mcNumber) {
            return
        }

        // Format the MC number for consistent lookup
        const formattedMCNumber = formatMCNumber(mcNumber)

        // Check if we already have this rating in Redux store
        if (rtsRatings[formattedMCNumber]) {
            return
        }

        const fetchRating = async () => {
            setIsLoading(true)
            setError(null)

            try {
                const response = await axios.get(`/integrations/rts/rating/${formattedMCNumber}`)

                if (response.data?.success && response.data?.data) {
                    const result = response.data.data
                    const data: RTSRatingData = {
                        rating: result.rating,
                        name: result.name,
                        mcNumber: result.mcNumber,
                        averageDaysToPayPast60: result.averageDaysToPayPast60,
                        city: result.city,
                        state: result.state,
                        lastActivity: result.lastActivity,
                        lastCreditScoreChange: result.lastCreditScoreChange,
                        phone: result.phone,
                        email: result.email,
                        dotNumber: result.dotNumber,
                        address: result.address1 + (result.address2 ? `, ${result.address2}` : ''),
                        collectionQueue: result.collectionQueue,
                        previousRating: result.previousRating,
                    }

                    // Store in Redux
                    dispatch(setRTSRating({ mcNumber: formattedMCNumber, ratingData: data }))
                }
            } catch (err) {
                console.error('Error in useRTSRating:', err)
                setError(err instanceof Error ? err : new Error('Unknown error'))
            } finally {
                setIsLoading(false)
            }
        }

        fetchRating()
    }, [mcNumber, dispatch, rtsRatings])

    // Get the rating data for this MC number from Redux
    const ratingData = mcNumber ? rtsRatings[formatMCNumber(mcNumber)] || null : null

    return { ratingData, isLoading, error }
}
